import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import * as React from "react";
import { FC } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    title = "title",
    links = "links",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.links]: LinkModel[];
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    pageType: getCurrentPage(state).type,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface LinkListStyles {
    linkStyles: InjectableCss;
    headingStyles: InjectableCss;
}

export const linkListStyles: LinkListStyles = {
    linkStyles: {
        css: css`
            padding: 5px;
        `,
    },
    headingStyles: {
        css: css`
            text-align: center;
            font-size: 2rem;
            font-family: "Poppins";
        `,
    },
};

const styles = linkListStyles;

export const UnorderedList = styled.ul<InjectableCss>`
    column-count: 4;
    display: block;
    padding: 10px 30px;
    border-radius: 0;
    ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(theme, [
            css`
                column-count: 1;
            `,
            css`
                column-count: 2;
            `,
            css`
                column-count: 3;
            `,
            css`
                column-count: 4;
            `,
            css`
                column-count: 4;
            `,
        ])}
    ${injectCss}
`;

const ListItem = styled.li`
    list-style: none;
`;

export const CENSiteMapLinks: FC<Props> = ({ fields }) => {
    const { links } = useGetLinks(fields.links, o => o.fields.destination);
    return (
        <StyledWrapper>
            <StyledWrapper>
                <Typography as="h2" {...styles.headingStyles}>
                    {fields.title}
                </Typography>
            </StyledWrapper>
            <UnorderedList>
                {links.map((link, index) => (
                    <ListItem key={index}>
                        {link.url && (
                            <Link
                                href={link.url}
                                {...styles.linkStyles}
                                target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                            >
                                {fields.links[index].fields.overriddenTitle || link.title}
                            </Link>
                        )}
                    </ListItem>
                ))}
            </UnorderedList>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, null)(CENSiteMapLinks),
    definition: {
        group: "Basic",
        icon: "ax-link-list",
        displayName: "CENSiteMapStaticLinks",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;

                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "overriddenTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
